import { Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { LeaderboardTrend } from "@/components/leaderboard/leaderboard-trend"
import { MomentumRingAvatar } from "@/components/rank/momentum-ring-avatar"
import { formatMonthYear } from "@/lib/format-month-year"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { CreatorLeaderboardRow } from "@/types/creator-leaderboard"

/**
 * OKS-519 — §13 forked-local rich leaderboard row.
 *
 * A FRESH local component (the shared `components/leaderboard/leaderboard-
 * row.tsx` stays for the unchanged brand-admin surface; the remote DS
 * `LeaderboardRow` is read-only). Embeds the OKS-512 `MomentumRingAvatar`
 * (with progress ring + rank-patch corner) — the design uses composed
 * rows, so we fork rather than bolt onto the flat shared row.
 *
 * # 2-line layout (UX Spec §2)
 *
 *   line 1: #rank · momentum avatar · name (+ "YOU" pill) · weekly XP · 🔥
 *   line 2: {named rank} · since {Mon YYYY} · ▲/▼/— delta
 *
 * # Ring mode
 *
 * `progress` present → `ring="progress"` (XP-to-next arc); top-tier
 * members (`progress: null`) → `ring="full"` (decorative, never an empty
 * arc), matching the profile/feed treatment.
 *
 * # Accessibility (UX Spec §6)
 *
 * The row carries an `aria-label` announcing rank + name + XP; the "YOU"
 * marker is a TEXT pill (not colour alone); the streak flame is
 * `aria-hidden` with the full label on the chip's `aria-label`; the delta
 * conveys direction via `LeaderboardTrend`'s text label.
 */
interface LeaderboardRowProps {
  row: CreatorLeaderboardRow
  /** True when this is the viewer's own row — accent treatment + "YOU" pill. */
  viewerHighlight?: boolean
}

export function LeaderboardRow({
  row,
  viewerHighlight = false,
}: LeaderboardRowProps) {
  const { t, i18n } = useTranslation()
  const formattedXp = formatXpNumber(row.weekly_xp, i18n.language)
  const since = formatMonthYear(row.joined_at, i18n.language)
  const ariaLabel = t("leaderboard.creator.row_aria", {
    rank: row.rank,
    name: row.display_name,
    xp: formattedXp,
  })

  return (
    <li
      data-testid={`creator-leaderboard-row-${row.rank}`}
      data-viewer={viewerHighlight ? "true" : undefined}
      aria-label={ariaLabel}
      className={cn(
        "flex items-center gap-3 rounded-xl px-3 py-2.5",
        viewerHighlight &&
          "border-primary bg-primary/5 border-l-4 pl-2.5 shadow-sm",
      )}
    >
      <span
        className="text-muted-foreground w-9 shrink-0 text-right text-sm font-semibold tabular-nums"
        aria-hidden="true"
      >
        #{row.rank}
      </span>

      <MomentumRingAvatar
        src={row.avatar_url}
        name={row.display_name}
        ring={row.progress ? "progress" : "full"}
        progress={row.progress}
        rankPatch={
          row.named_rank
            ? {
                tier: row.tier,
                tierCount: row.tier_count,
                name: row.named_rank,
              }
            : undefined
        }
        size="sm"
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium" title={row.display_name}>
            {row.display_name}
          </span>
          {viewerHighlight && (
            <span
              data-testid="creator-leaderboard-you-pill"
              className="bg-primary text-primary-foreground shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wide"
            >
              {t("leaderboard.creator.you")}
            </span>
          )}
        </div>
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
          {row.named_rank && <span className="truncate">{row.named_rank}</span>}
          {row.named_rank && since && <span aria-hidden="true">·</span>}
          {since && (
            <span className="shrink-0">
              {t("leaderboard.creator.since", { date: since })}
            </span>
          )}
          <LeaderboardTrend trend={row.trend} className="w-4" />
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-sm font-semibold tabular-nums">
          {formattedXp}{" "}
          <span className="text-muted-foreground text-xs font-normal">
            {t("leaderboard.xp_suffix")}
          </span>
        </span>
        {row.streak_days > 0 && (
          <span
            data-testid="creator-leaderboard-streak"
            className="text-muted-foreground flex items-center gap-0.5 text-xs tabular-nums"
            aria-label={t("leaderboard.creator.streak_aria", {
              count: row.streak_days,
            })}
          >
            <Flame className="size-3 text-orange-500" aria-hidden="true" />
            {row.streak_days}
          </span>
        )}
      </div>
    </li>
  )
}
